import ProgressTrackerChip from "@/app/components/cards/ProgressTrackerChip";
import { ContentRenderer } from "@/lib/content/ContentRenderer";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import { userHasAdminRights } from "@/lib/utils/pageChecks";
import { Paper, Text, Title } from "@mantine/core";
import { NoteSection } from "@tsTypes/note-schema";
import clsx from "clsx";
import { getUserRole } from "lib/utils/accessChecks";
import { useSession } from "next-auth/react";
import { useEffect, useRef } from "react";
import {
  noteView,
  selectNoteById,
} from "../../../../../../../../features/progress/subTopicUserReduxMetadataSlice";
import {
  useCustomDispatch,
  useCustomSelector,
} from "../../../../../../../../lib/redux/hooks";
import ResourceEditInStrapiButton from "../../../questions/[topic]/components/Header/ResourceEditInStrapiButton";
import NoteBranding from "./NoteBranding";
import classes from "./NotePage.module.css";

type NotePageProps = {
  subjectNameLower: string;
  subjectDocumentId: string;
  topicDocumentId: string;
  subTopicDocumentId: string;
  pageDocumentId: string;
  sectionContent: NoteSection;
  // highlight: Boolean;
  disableActions: boolean;
  isPreviewPortal?: boolean;
};

export const useNotePageStyles = ({
  subjectColorIndex,
}: {
  subjectColorIndex: string;
}) => {
  const style = {
    "--np-subject-2": `var(--mantine-color-${subjectColorIndex}-2)`,
    "--np-subject-4": `var(--mantine-color-${subjectColorIndex}-4)`,
    "--np-subject-5": `var(--mantine-color-${subjectColorIndex}-5)`,
    "--np-subject-6": `var(--mantine-color-${subjectColorIndex}-6)`,
  } as React.CSSProperties;
  return { classes, style };
};

export default function NotePage({
  subjectNameLower,
  subjectDocumentId,
  topicDocumentId,
  subTopicDocumentId,
  pageDocumentId,
  sectionContent,
  // highlight,
  disableActions,
  isPreviewPortal,
}: NotePageProps) {
  const { data: session } = useSession();
  const userRole = getUserRole(session);
  const userHasAdminEditAccess =
    userHasAdminRights(userRole) || userRole === "Content Creator";
  const subjectColorIndex = getSubjectColorIndex(subjectNameLower);
  const { style } = useNotePageStyles({ subjectColorIndex });
  const sectionRef = useRef(null);
  const dispatch = useCustomDispatch();

  const noteMeta = useCustomSelector((state) =>
    selectNoteById(
      state,
      subjectDocumentId,
      topicDocumentId,
      subTopicDocumentId,
      sectionContent.customDocumentId
    )
  );
  const lastRead = noteMeta?.dr[noteMeta.dr.length - 1];

  function handleNoteSectionView() {
    // only log a read if it has been >5m since last read
    if (!lastRead || Date.now() - lastRead > 1000 * 60 * 3) {
      dispatch(
        noteView({
          topicDocumentId,
          subjectDocumentId,
          noteSectionId: sectionContent.customDocumentId,
          subTopicDocumentId,
          date: Date.now(),
        })
      );
    }
  }

  useEffect(() => {
    if (disableActions || !sectionContent) return;
    let noteTimeout: any;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            noteTimeout = setTimeout(() => handleNoteSectionView(), 5000); // after 5s of being visible, mark as read
          } else {
            clearTimeout(noteTimeout);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      observer.disconnect();
      clearTimeout(noteTimeout);
    };
  }, [sectionContent, disableActions]);

  return (
    <Paper
      withBorder
      className={clsx(classes.notePage, "definitionBoundary")}
      style={style}
      ref={sectionRef}
      id={sectionContent.customDocumentId}
    >
      <span className={classes.progressTrackerChipWrapper}>
        <ProgressTrackerChip
          resource="notes"
          disableActions={disableActions}
          subjectName={subjectNameLower}
          subjectDocumentId={subjectDocumentId}
          resourceReduxMeta={noteMeta}
          noResource={!sectionContent}
        />
      </span>
      {userHasAdminEditAccess && (
        <span className={classes.editButtonWrapper}>
          <ResourceEditInStrapiButton
            resource={"notes"}
            resourceDocumentId={pageDocumentId}
          />
        </span>
      )}
      <Title order={4}>{sectionContent.Title}</Title>
      <Text
        component="div"
        id={`P_${pageDocumentId}_S_${sectionContent.customDocumentId}`}
        className={classes.noteSection}
      >
        <ContentRenderer content={sectionContent.Notes} imageContext="notes" />
      </Text>
      <NoteBranding subjectColorIndex={subjectColorIndex} />
    </Paper>
  );
}
